import { logger, trackWorkerExceptions } from '@fe/core';
import { WeatherLoader } from '@fe/weather';
import { Unsubscribe } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { EventBus } from '../app/contexts/event-bus';
import { levelToKeyMap } from '../app/features/settings/components/sub-pages/monitor-alert/common/common';
import { createAppSelector } from '../app/hooks/store-hooks';
import { playbackProgress, playbackSeekEnd } from '../app/state/reducers/playback';
import { stopRecording } from '../app/state/reducers/recording';
import { diversionSettingsSelector } from '../app/state/selectors';
import { createDrawingObject, deleteDrawingObject, updateDrawingObject } from '../app/state/slices/drawing-objects-slice';
import {
    addNotification,
    addPopupAlerts,
    NotificationType,
    setIntegrationState,
    setMyAirports,
    workerFlightsUpdated,
    workerSetMyFleetLoading,
    workerUpdateState
} from '../app/state/slices/global-slice';
import { setRights } from '../app/state/slices/user-slice';
import { initWeatherProducts, updateWeatherAccessInfo, updateWeatherProducts } from '../app/state/slices/weather-slice';
import { updateFlightRoutes, updateSelectedFlightDetails, upsertPinnedFlight } from '../app/state/slices/workspace-slice';
import { store, subscribeStoreWithSelector } from '../app/state/store';
import { FPMMessageType } from '../common/enums';
import { DrawingObjectCategory } from '../components/drawing-object/types';
import { storeAreaToDrawingObject } from '../components/helpers/helpers';
import { playSound } from '../monitoring/alerts/sound/play-sound';
import { AircraftMonitorSettingsMerger, AircraftThresholdEffective } from '../monitoring/merger/aircraft-threshold-merger';
import { AirportMonitorSettingsMerger, AirportThresholdEffective } from '../monitoring/merger/airport-threshold-merger';
import { mergeAircraftOptions, mergeAirportOptions } from '../monitoring/merger/monitor-options-merger';
import {
    WorkerAircraftMonitorThresholdAlerts,
    WorkerAirportThresholdGroup,
    WorkerAirportThresholdGroupItem,
    WorkerAirportThresholdSettingsItem,
    WorkerMonitorAircraftThreshold,
    WorkerMonitorAirportThresholdAlerts,
    WorkerMonitorSettings,
    WorkerThresholdLevel
} from '../monitoring/models';
import { Actor } from '../services/actor';
import { MessageType } from '../services/actor-messages';
import { DiversionManagerService } from '../services/axios/axios-api-v2-generated';
import { fpmRequestTracker, getIntegrationName } from '../services/integration/fpm-request-tracker';
import { fpmRouteImporter } from '../services/integration/fpm-route-importer';
import { sendEmailAlerts } from '../services/monitor-alert-email-service';
import { MyFleetSync } from '../services/my-fleet-sync';
import { downloadNotification } from '../services/systemhub-service';
import { timeService } from '../services/time/time-service';
import voiceService from '../services/voice-service';
import { toSource } from '../services/weather/helpers';
import { LogFileSettings, TimeSettings, UnitSettings } from '../state/settings';
import { StateManager } from '../state/state-manager';
import { FilterGroupType } from '../types/filter-group';
import {
    CompanyThresholdsMode,
    DECIMAL_THRESHOLD_DIVISOR,
    MonitorAircraftThresholdAlerts,
    MonitorAirportThresholdAlerts,
    MonitorSettings,
    MonitorThresholdAlertMinimumLevel,
    ThresholdControlType,
    ThresholdEnableState,
    ThresholdInfo,
    ThresholdLevel
} from '../types/thresholds';
import { MyAirportSettings } from '../udView/udview';
import Worker from '../worker/flight-worker.ts?worker';
import { type App } from './app';

dayjs.extend(utc);
dayjs.extend(tz);

/**
 * Wraps the fe comm worker on the window side.
 * Syncs the state of settings etc to the worker.
 */
export class WorkerClient {
    private worker: Worker;
    public readonly actor: Actor;
    public readonly myFleetSync: MyFleetSync;

    private initPromiseResolve: (() => void) | null = null;
    public readonly initPromise: Promise<void>;
    private subscriptions: Unsubscribe[] = [];

    constructor(private readonly app: App) {
        this.initPromise = new Promise((resolve) => (this.initPromiseResolve = resolve));
        this.worker = new Worker({ name: 'fe-comm-worker' });

        trackWorkerExceptions(this.worker);
        this.actor = new Actor(this.worker);
        this.myFleetSync = new MyFleetSync(this.actor);

        this.actor.registerMessageHandler(MessageType.start, async () => {
            this.sendInitialSettings();
            this.addStoreSubscriptions();
            // Resolve the init promise
            this.initPromiseResolve?.call(undefined);
            this.initPromiseResolve = null;
        });

        this.actor.registerMessageHandler(MessageType.userRightsUpdated, async (data) => {
            store.dispatch(setRights(data));
        });
        this.actor.registerMessageHandler(MessageType.FPMIntegrationStatusChanged, async (data) => {
            store.dispatch(setIntegrationState({ status: data.status, desktopAppName: data.desktopAppName }));
        });
        this.actor.registerMessageHandler(MessageType.appStateChanged, async (data) => {
            store.dispatch(workerUpdateState({ state: data }));
        });
        this.actor.registerMessageHandler(MessageType.systemHubReload, async () => downloadNotification());
        this.actor.registerMessageHandler(MessageType.sendEmailAlert, async (data) =>
            sendEmailAlerts(data.email, data.subject, data.content)
        );
        this.actor.registerMessageHandler(MessageType.sendZoomAlert, async (data) =>
            EventBus.fire('alert-zoom-to-aircraft', { id: data.id })
        );
        this.actor.registerMessageHandler(MessageType.updateEnd, async (data) => {
            store.dispatch(workerFlightsUpdated({ stats: data, time: timeService.currentTime }));
            EventBus.fire('update-end', {});
            if (data.airportsChanged) {
                EventBus.fire('airports-changed', {});
            }
        });
        this.actor.registerMessageHandler(MessageType.sendIntersectingFlightsResponse, async (data) => {
            EventBus.fire('FAIResponse', data);
        });
        this.actor.registerMessageHandler(MessageType.sendPopupAlert, async (data) => {
            store.dispatch(addPopupAlerts({ alerts: data }));
        });
        this.actor.registerMessageHandler(MessageType.queueVoiceAlert, async (data) => voiceService.queueMessage(data));
        this.actor.registerMessageHandler(MessageType.sendSoundAlert, async () => playSound());
        this.actor.registerMessageHandler(MessageType.playbackProgress, async (progress) => store.dispatch(playbackProgress(progress)));
        this.actor.registerMessageHandler(MessageType.playbackSeekEnded, async () => store.dispatch(playbackSeekEnd()));
        this.actor.registerMessageHandler(MessageType.getRecordingSettings, async () => {
            const settings = store.getState().settings;
            return {
                // TODO: just use existing infra to keep this in sync with worker
                splitAtMidnight: settings.splitRecordingAtMidnight
            };
        });

        this.actor.registerMessageHandler(MessageType.recordingError, async (x) => {
            store.dispatch(stopRecording());
            store.dispatch(addNotification({ type: NotificationType.generalError, customMessage: x.message ?? 'Recording error' }));
        });

        this.actor.registerMessageHandler(MessageType.recordingFileSplit, async (x) => {
            if (x.type === 'file') {
                // Change directory for weather files
                WeatherLoader.setRecordingDirectory(x.directoryHandle);
            }
        });

        this.actor.registerMessageHandler(MessageType.airportConfigUpdated, async (data) => {
            EventBus.fire('airport-config-updated', data);
        });

        this.actor.registerMessageHandler(MessageType.airportMetarUpdated, async (data) => {
            EventBus.fire('airport-metar-updated', data);
        });

        this.actor.registerMessageHandler(MessageType.airportRvrUpdated, async (data) => {
            EventBus.fire('airport-rvr-updated', data);
        });

        this.actor.registerMessageHandler(MessageType.FPMMessageReceived, async (message) => {
            switch (message.cSubCode) {
                case FPMMessageType.StoreArea: {
                    logger.info('FPM StoreArea message received', message.pczObjID);
                    const existingObject =
                        store
                            .getState()
                            .drawingObjects.objects.find(
                                (obj) => obj.name === message.pczObjID && obj.category === DrawingObjectCategory.avoidance && !obj.isCompany
                            ) ?? null;
                    const drawingObject = storeAreaToDrawingObject(message, existingObject);
                    if (existingObject) {
                        logger.info('FPM StoreArea message updating existing object', existingObject.id);
                        store.dispatch(updateDrawingObject(drawingObject));
                    } else {
                        logger.info('FPM StoreArea message creating new object', drawingObject.name);
                        store.dispatch(createDrawingObject(drawingObject));
                    }
                    break;
                }
                case FPMMessageType.DeleteArea: {
                    logger.info('FPM DeleteArea message received', message.pczObjID);
                    const objectToDelete = store
                        .getState()
                        .drawingObjects.objects.find(
                            (obj) => obj.name === message.pczObjID && obj.category === DrawingObjectCategory.avoidance && !obj.isCompany
                        );
                    if (objectToDelete) {
                        store.dispatch(deleteDrawingObject(objectToDelete.id));
                    } else {
                        logger.warn('FPM DeleteArea message received for non-existing object', message.pczObjID);
                    }
                    break;
                }
                case FPMMessageType.RouteResponse:
                case FPMMessageType.CalculateRouteRs:
                    fpmRouteImporter.handleRouteResponse(message);
                    EventBus.fire('fpm-route-response', message);
                    break;
                case FPMMessageType.VerifyRouteResponse:
                    fpmRouteImporter.handleVerifyResponse(message);
                    EventBus.fire('fpm-verify-route-response', message);
                    break;
                case FPMMessageType.PlannedRouteResponse:
                    fpmRouteImporter.handlePlannedRoute(message);
                    EventBus.fire('fpm-planned-route-response', message);
                    break;
                case FPMMessageType.TextInfo:
                case FPMMessageType.GoToFlightRs:
                case FPMMessageType.CalculateRouteNotif:
                    fpmRequestTracker.handleMessage(message);
                    break;
            }
        });

        this.actor.registerMessageHandler(MessageType.FPMGoToFlightReceived, async ({ flightId, acid }) => {
            logger.info('FPM GoToFlight message received', flightId, acid);
            if (flightId === null) {
                store.dispatch(
                    addNotification({
                        type: NotificationType.generalWarning,
                        customMessage: `${getIntegrationName()} requested flight ${acid}, but it was not found in Flight Explorer`
                    })
                );
                return;
            }
            EventBus.defer('selectedMap', 'select-flight', { flightId, options: { source: 'fpm' } });
        });

        this.actor.registerMessageHandler(MessageType.diversionRecordsUpdated, async () => {
            logger.info('diversionRecordsUpdated message received');
            EventBus.fire('diversion-record-updated', null);
        });

        this.actor.registerMessageHandler(MessageType.getDiversionCapacity, async () => {
            try {
                return await DiversionManagerService.getInfoAsync({ withAuthorization: true });
            } catch (err) {
                logger.trackException(err);
                return null;
            }
        });

        this.actor.registerMessageHandler(MessageType.myFleetLoading, (data) => {
            store.dispatch(workerSetMyFleetLoading({ loading: data.loading }));
            return Promise.resolve();
        });

        this.actor.registerMessageHandler(MessageType.weatherInit, (msg) => {
            WeatherLoader.unloadAll(); // Unload all products on init
            store.dispatch(initWeatherProducts(msg));

            // FIXME: this is a bit of a mess but that's the best we can do atm. Weather had things piling up for a while now and needs a refactor
            self.setTimeout(() => {
                EventBus.fire('reload-all-weather', undefined);
            }, 100);
            return Promise.resolve();
        });

        this.actor.registerMessageHandler(MessageType.weatherUpdated, (msg) => {
            store.dispatch(updateWeatherProducts(msg));
            return Promise.resolve();
        });

        this.actor.registerMessageHandler(MessageType.weatherServerAccessInfoReceived, (msg) => {
            store.dispatch(updateWeatherAccessInfo(msg));
            if (store.getState().global.playback.active) {
                // In playback we load from files
                return Promise.resolve();
            }

            WeatherLoader.setSource(toSource(msg));
            return Promise.resolve();
        });

        this.actor.registerMessageHandler(MessageType.watchedFlightsUpdated, (data) => {
            // TODO: deleted
            store.dispatch(upsertPinnedFlight(data.pinned));
            store.dispatch(updateSelectedFlightDetails(data.full));
            return Promise.resolve();
        });

        this.actor.registerMessageHandler(MessageType.flightRouteUpdated, async (data) => {
            store.dispatch(updateFlightRoutes(data));
            EventBus.fire('flight-routes-updated', data);
        });

        this.actor.registerMessageHandler(MessageType.myAirportsChanged, (data) => {
            logger.info('My airports changed', data);
            store.dispatch(setMyAirports(data.airports));
            return Promise.resolve();
        });
    }

    /**
     * Sends initial settings to the worker. E.g. time format settings, ud view, monitor settings etc.
     */
    private sendInitialSettings() {
        // build env
        const state = store.getState();

        this.actor.sendAsync({
            type: MessageType.setWorkspaces,
            data: state.workspace.items.map((x) => ({ workspaceId: x.id, view: x.view }))
        });

        this.actor.sendAsync({ type: MessageType.myFleetChanged, data: this.myFleetSync.getMyFleetSettings() });
        this.actor.sendAsync({
            type: MessageType.unitsChanged,
            data: {
                fobVolume: state.settings.flightFobVolumeDisplayUnit,
                fobWeight: state.settings.flightFobWeightDisplayUnit,
                latLngFormat: state.settings.latLongDisplayFormat,
                distanceUnit: state.settings.distanceUnit
            }
        });
        this.actor.sendAsync({
            type: MessageType.timeSettingsChanged,
            data: {
                timeZone: state.settings.timeDisplayZone,
                timeFormat: state.settings.timeDisplayFormat,
                dateFormat: state.settings.dateDisplayFormat,
                dateDelimiter: state.settings.dateDelimiter,
                showDaysWithTime: state.settings.showDayWithTimes
            }
        });
        // TODO: remove this state from worker
        this.actor.sendAsync({ type: MessageType.tooltipFieldsChanged, data: state.settings.tooltipFields });
        this.actor.sendAsync({
            type: MessageType.logFileSettingsUpdated,
            data: {
                addSpaces: state.settings.addSpaceAfterCommaLogFile,
                addHeadingLine: state.settings.addHeadingLogFile,
                timeZone: state.settings.timeZoneLogFile,
                timeFormat: state.settings.timeFormatLogFile,
                sanitizeTailNumbers: state.settings.sanitizeTailNumbersLogFile,
                includeScratchPad: state.settings.includeScratchPadLogFile,
                positionFormat: state.settings.positionFormatLogFile
            }
        });

        this.actor.sendAsync({
            type: MessageType.myAirportSettingsChanged,
            data: {
                filterGroup: state.filterGroups.groups[FilterGroupType.airport].find((x) => x.id === state.settings.myAirportFilterGroup),
                includeOrgDestAlt: state.settings.myAirportIncludeMyFleetOrgDestAlt,
                includeScheduledMyFleet: state.settings.myAirportsIncludeScheduledMyFleet
            }
        });

        this.actor.sendAsync({
            type: MessageType.updateMonitorSettings,
            data: monitorSettingsToWorkerMonitorSettings(state.global.monitorSettings, state.user.rights.companyFeaturesEnabled)
        });
        this.actor.sendAsync({ type: MessageType.setDiversionSettings, data: diversionSettingsSelector(state) });
        this.actor.sendAsync({ type: MessageType.aircraftEdrSettingsChanged, data: state.settings.aircraftEdrSettings });

        // Drawing objects
        this.actor.sendAsync({
            type: MessageType.drawingObjectsUpdated,
            data: {
                updated: [],
                deleted: [],
                added: state.drawingObjects.objects,
                objectsToDisableFpmSync: []
            }
        });

        // TODO: use redux store directly instead of StateManager
        StateManager.updateUnits({
            fobVolume: state.settings.flightFobVolumeDisplayUnit,
            fobWeight: state.settings.flightFobWeightDisplayUnit,
            latLngFormat: state.settings.latLongDisplayFormat,
            distanceUnit: state.settings.distanceUnit
        });

        // TODO: use redux store directly instead of StateManager
        StateManager.updateTime({
            timeZone: state.settings.timeDisplayZone,
            timeFormat: state.settings.timeDisplayFormat,
            dateFormat: state.settings.dateDisplayFormat,
            dateDelimiter: state.settings.dateDelimiter,
            showDaysWithTime: state.settings.showDayWithTimes
        });

        this.actor.sendAsync({
            type: MessageType.debugFlightsEnableDisable,
            data: {
                enabled:
                    state.settings.enableDebugFeatures &&
                    state.settings.enableMessageDebugger &&
                    (import.meta.env.DEV || state.user.rights.showCommandPrototype)
            }
        });

        this.actor.sendAsync({
            type: MessageType.debugIntegrationMessagesEnableDisable,
            data: { enabled: state.settings.enableDebugFeatures && state.settings.enableIntegrationMessageDebugger }
        });

        state.settings.debugFlightIds.forEach((x) =>
            this.actor.sendAsync({
                type: MessageType.debugFlightMessageSetFlight,
                data: { type: 'add', flightId: x }
            })
        );

        this.actor.sendAsync({
            type: MessageType.emailAlertSettingsChanged,
            data: {
                mainEmail: state.settings.alertEmailToAddress,
                subject: state.settings.alertEmailSubject,
                customEmails: state.settings.alertCustomEmailAlerts
            }
        });
    }

    /**
     * Adds redux store subscriptions
     */
    private addStoreSubscriptions() {
        // Sync time settings
        // TODO: sync with state manager for now, but when state manager is removed use a hook directly in react code
        this.subscriptions.push(
            subscribeStoreWithSelector(timeSettingsSelector, (s) => {
                StateManager.updateTime(s);
                this.actor.sendAsync({ type: MessageType.timeSettingsChanged, data: s });
            })
        );

        // Log file settings
        this.subscriptions.push(
            subscribeStoreWithSelector(logFileSettingsSelector, (settings) =>
                this.actor.sendAsync({
                    type: MessageType.logFileSettingsUpdated,
                    data: settings
                })
            )
        );

        // TODO: sync with state manager for now, but when state manager is removed use a hook directly in react code
        // Sync units
        this.subscriptions.push(
            subscribeStoreWithSelector(unitSettingsSelector, (s) => {
                StateManager.updateUnits(s);
                this.actor.sendAsync({ type: MessageType.unitsChanged, data: s });
            })
        );

        // Sync my airports settings
        this.subscriptions.push(
            subscribeStoreWithSelector(myAirportSelector, (s) => {
                this.actor.sendAsync({ type: MessageType.myAirportSettingsChanged, data: s });
            })
        );

        // Sync tooltip fields
        this.subscriptions.push(
            subscribeStoreWithSelector(
                (x) => x.settings.tooltipFields,
                (s) => this.actor.sendAsync({ type: MessageType.tooltipFieldsChanged, data: s })
            )
        );

        // Sync monitor settings
        this.subscriptions.push(
            subscribeStoreWithSelector(
                createAppSelector(
                    [(x) => x.global.monitorSettings, (x) => x.user.rights.companyFeaturesEnabled],
                    (settings, companyFeaturesEnabled) => ({ settings, companyFeaturesEnabled })
                ),
                ({ settings, companyFeaturesEnabled }) =>
                    this.actor.sendAsync({
                        type: MessageType.updateMonitorSettings,
                        data: monitorSettingsToWorkerMonitorSettings(settings, companyFeaturesEnabled)
                    })
            )
        );

        // Sync diversion settings
        this.subscriptions.push(
            subscribeStoreWithSelector(diversionSettingsSelector, (s) =>
                this.actor.sendAsync({
                    type: MessageType.setDiversionSettings,
                    data: s
                })
            )
        );

        // Sync aircraft EDR settings
        this.subscriptions.push(
            subscribeStoreWithSelector(
                (state) => state.settings.aircraftEdrSettings,
                (s) =>
                    this.actor.sendAsync({
                        type: MessageType.aircraftEdrSettingsChanged,
                        data: s
                    })
            )
        );

        //Sync notes summary
        this.subscriptions.push(
            subscribeStoreWithSelector(
                (state) => state.notes.aircraftWithNotes,
                (s) => this.actor.sendAsync({ type: MessageType.flightsWithNotesChanged, data: s })
            )
        );
        this.subscriptions.push(
            subscribeStoreWithSelector(
                (state) => state.notes.airportsWithNotes,
                (s) => this.actor.sendAsync({ type: MessageType.airportsWithNotesChanged, data: s })
            )
        );

        this.subscriptions.push(
            EventBus.on('drawing-objects-updated', (diff) => {
                this.actor.sendAsync({ type: MessageType.drawingObjectsUpdated, data: diff });
            }).unsubscribe
        );

        // Debug state
        this.subscriptions.push(
            subscribeStoreWithSelector(
                (s) =>
                    s.settings.enableDebugFeatures &&
                    s.settings.enableMessageDebugger &&
                    // In DEV mode ignore command prototype right
                    (import.meta.env.DEV || s.user.rights.showCommandPrototype),
                (enabled) => this.actor.sendAsync({ type: MessageType.debugFlightsEnableDisable, data: { enabled } })
            )
        );

        // Email settings
        this.subscriptions.push(
            subscribeStoreWithSelector(
                createAppSelector(
                    [(s) => s.settings.alertEmailToAddress, (s) => s.settings.alertEmailSubject, (s) => s.settings.alertCustomEmailAlerts],
                    (mainEmail, subject, customEmails) => ({
                        mainEmail,
                        subject,
                        customEmails
                    })
                ),
                (settings) => this.actor.sendAsync({ type: MessageType.emailAlertSettingsChanged, data: settings })
            )
        );
    }

    remove() {
        this.worker.terminate();
        this.actor.remove();
        this.subscriptions.forEach((x) => x());
        this.myFleetSync.finalize();
        this.subscriptions = [];
    }
}

const timeSettingsSelector = createAppSelector(
    [
        (state) => state.settings.timeDisplayZone,
        (state) => state.settings.timeDisplayFormat,
        (state) => state.settings.dateDisplayFormat,
        (state) => state.settings.dateDelimiter,
        (state) => state.settings.showDayWithTimes
    ],
    (timeZone, timeFormat, dateFormat, delim, showDays) =>
        ({
            timeZone: timeZone,
            timeFormat: timeFormat,
            dateFormat: dateFormat,
            dateDelimiter: delim,
            showDaysWithTime: showDays
        }) satisfies TimeSettings
);

const logFileSettingsSelector = createAppSelector(
    [
        (state) => state.settings.addSpaceAfterCommaLogFile,
        (state) => state.settings.addHeadingLogFile,
        (state) => state.settings.timeZoneLogFile,
        (state) => state.settings.timeFormatLogFile,
        (state) => state.settings.sanitizeTailNumbersLogFile,
        (state) => state.settings.includeScratchPadLogFile,
        (state) => state.settings.positionFormatLogFile
    ],
    (
        addSpaceAfterCommaLogFile,
        addHeadingLogFile,
        timeZoneLogFile,
        timeFormatLogFile,
        sanitizeTailNumbersLogFile,
        includeScratchPadLogFile,
        positionFormatLogFile
    ) =>
        ({
            addSpaces: addSpaceAfterCommaLogFile,
            addHeadingLine: addHeadingLogFile,
            timeZone: timeZoneLogFile,
            timeFormat: timeFormatLogFile,
            sanitizeTailNumbers: sanitizeTailNumbersLogFile,
            includeScratchPad: includeScratchPadLogFile,
            positionFormat: positionFormatLogFile
        }) satisfies LogFileSettings
);

const unitSettingsSelector = createAppSelector(
    [
        (state) => state.settings.flightFobVolumeDisplayUnit,
        (state) => state.settings.flightFobWeightDisplayUnit,
        (state) => state.settings.latLongDisplayFormat,
        (state) => state.settings.distanceUnit
    ],
    (fobVolume, fobWeight, latLngFormat, distance) =>
        ({
            fobVolume: fobVolume,
            fobWeight: fobWeight,
            latLngFormat: latLngFormat,
            distanceUnit: distance
        }) satisfies UnitSettings
);

const myAirportSelector = createAppSelector(
    [
        (state) => state.settings.myAirportIncludeMyFleetOrgDestAlt,
        (state) => state.settings.myAirportsIncludeScheduledMyFleet,
        (state) =>
            state.settings.myAirportFilterGroup
                ? state.filterGroups.groups[FilterGroupType.airport].find((x) => x.id === state.settings.myAirportFilterGroup)
                : undefined
    ],
    (includeOrDestAlt, includeScheduledMyFleet, filterGroup) =>
        ({
            includeOrgDestAlt: includeOrDestAlt,
            filterGroup: filterGroup,
            includeScheduledMyFleet: includeScheduledMyFleet
        }) satisfies MyAirportSettings
);

function monitorSettingsToWorkerMonitorSettings(monitorSettings: MonitorSettings, companyFeaturesEnabled: boolean): WorkerMonitorSettings {
    const mode = companyFeaturesEnabled ? monitorSettings.company.mode : CompanyThresholdsMode.notUsed;
    const aircraftMerger = new AircraftMonitorSettingsMerger();
    aircraftMerger.setDefinitions(monitorSettings.thresholds.aircraft.definitions);
    aircraftMerger.setMode(mode);
    aircraftMerger.setData({
        user: monitorSettings.user.aircraft.thresholds,
        company: monitorSettings.company.aircraft.thresholds
    });

    const airportMerger = new AirportMonitorSettingsMerger();
    airportMerger.setThresholdDefinitions(monitorSettings.thresholds.airport.definitions);
    airportMerger.setMode(mode);
    airportMerger.setData({
        user: monitorSettings.user.airport,
        company: monitorSettings.company.airport
    });
    const aircraftMerged = aircraftMerger.getThresholdsFor('user');
    const aircraftMonitorOptions = mergeAircraftOptions(
        'user',
        {
            user: monitorSettings.user.aircraft.options,
            company: monitorSettings.company.aircraft.options
        },
        mode
    );

    const airportGroups = airportMerger.getGroups('user');
    const airportThresholdOptions = airportMerger.getThresholdOptions('user');
    const airportMonitorOptions = mergeAirportOptions(
        'user',
        { user: monitorSettings.user.airport.options, company: monitorSettings.company.airport.options },
        monitorSettings.company.mode
    );

    const aircraftThresholds = Object.values(aircraftMerged)
        .filter(
            (x) =>
                x.effective &&
                x.effective.enableState === ThresholdEnableState.enabled &&
                (monitorSettings.thresholds.aircraft.definitions[x.type].alertsOnly || anyLevelDefined(x.effective))
        )
        .map((threshold) => {
            const info = monitorSettings.thresholds.aircraft.definitions[threshold.type];
            return {
                type: threshold.type,
                priority: threshold.effective!.priority,
                alerts: toWorkerAircraftAlerts(threshold.effective!.alerts),
                info: toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.info),
                watch: toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.watch),
                warning: toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.warning),
                critical: toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.critical),
                emergency: toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.emergency)
            } satisfies WorkerMonitorAircraftThreshold;
        });

    const airportThresholdSettings: WorkerAirportThresholdSettingsItem[] = airportThresholdOptions.map(
        (option) =>
            ({
                type: option.type,
                priority: option.priority,
                alerts: toWorkerAirportAlerts(option.alerts)
            }) satisfies WorkerAirportThresholdSettingsItem
    );

    return {
        aircraftThresholds: aircraftThresholds,
        aircraftThresholdsTypeLookup: Object.fromEntries(aircraftThresholds.map((x) => [x.type, x])),
        airportThresholds: {
            airports: airportGroups
                .filter((x) => !x.isOff)
                .map(
                    (group) =>
                        ({
                            airportId: group.airportId || null,
                            usage: group.usage,
                            thresholds: Object.values(airportMerger.getThresholdsFor('user', group.airportId, group.usage))
                                .filter((x) => x.effective && x.effective.enableState === ThresholdEnableState.enabled)
                                .map((threshold) => {
                                    const info = monitorSettings.thresholds.airport.definitions[threshold.type];
                                    return {
                                        type: threshold.type,
                                        levels: [
                                            toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.info),
                                            toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.watch),
                                            toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.warning),
                                            toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.critical),
                                            toWorkerMonitorLevel(info, threshold.effective, ThresholdLevel.emergency)
                                        ]
                                    } satisfies WorkerAirportThresholdGroupItem;
                                })
                        }) satisfies WorkerAirportThresholdGroup
                ),
            typeLookup: Object.fromEntries(airportThresholdSettings.map((x) => [x.type, x])),
            settings: airportThresholdSettings
        },
        options: {
            ...aircraftMonitorOptions,
            ...airportMonitorOptions
        },
        settings: {
            aircraft: {
                definitions: monitorSettings.thresholds.aircraft.definitions
            },
            airport: {
                definitions: monitorSettings.thresholds.airport.definitions
            },
            eventDefinitions: monitorSettings.eventDefinitions
        }
    };
}

function toWorkerAircraftAlerts(alerts: MonitorAircraftThresholdAlerts): WorkerAircraftMonitorThresholdAlerts {
    return {
        eventList: {
            value: alerts.eventList.value ?? false,
            minLevel: alerts.eventList.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        eventListAck: {
            value: alerts.eventListAck.value ?? false,
            minLevel: alerts.eventListAck.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        eventListImportant: {
            value: alerts.eventListImportant.value ?? false,
            minLevel: alerts.eventListImportant.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        email: {
            value: alerts.email.value ?? false,
            minLevel: alerts.email.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        logFile: {
            value: alerts.logFile.value ?? false,
            minLevel: alerts.logFile.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        popup: {
            value: alerts.popup.value ?? false,
            minLevel: alerts.popup.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        osAlert: {
            value: alerts.osAlert.value ?? false,
            minLevel: alerts.osAlert.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        overrideColor: {
            value: alerts.overrideColor.value ?? false,
            minLevel: alerts.overrideColor.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        voice: {
            value: alerts.voice.value ?? false,
            minLevel: alerts.voice.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        playSound: {
            value: alerts.playSound.value ?? false,
            minLevel: alerts.playSound.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        overrideColorHex: alerts.overrideColorHex,
        aircraftTag: {
            value: alerts.aircraftTag.value ?? false,
            minLevel: alerts.aircraftTag.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        zoomToAircraft: {
            value: alerts.zoomToAircraft.value ?? false,
            minLevel: alerts.zoomToAircraft.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        enableMarker: {
            value: alerts.enableMarker.value ?? false,
            minLevel: alerts.enableMarker.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        alertOnScheduled: alerts.alertOnScheduled.value ?? false,
        alertOnPlanned: alerts.alertOnPlanned.value ?? false,
        alertOnActive: alerts.alertOnActive.value ?? false
    } satisfies WorkerAircraftMonitorThresholdAlerts;
}

function toWorkerAirportAlerts(alerts: MonitorAirportThresholdAlerts): WorkerMonitorAirportThresholdAlerts {
    return {
        eventList: {
            value: alerts.eventList.value ?? false,
            minLevel: alerts.eventList.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        eventListAck: {
            value: alerts.eventListAck.value ?? false,
            minLevel: alerts.eventListAck.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        eventListImportant: {
            value: alerts.eventListImportant.value ?? false,
            minLevel: alerts.eventListImportant.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        email: {
            value: alerts.email.value ?? false,
            minLevel: alerts.email.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        logFile: {
            value: alerts.logFile.value ?? false,
            minLevel: alerts.logFile.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        popup: {
            value: alerts.popup.value ?? false,
            minLevel: alerts.popup.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        osAlert: {
            value: alerts.osAlert.value ?? false,
            minLevel: alerts.osAlert.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        overrideColor: {
            value: alerts.overrideColor.value ?? false,
            minLevel: alerts.overrideColor.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        voice: {
            value: alerts.voice.value ?? false,
            minLevel: alerts.voice.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        playSound: {
            value: alerts.playSound.value ?? false,
            minLevel: alerts.playSound.minLevel ?? MonitorThresholdAlertMinimumLevel.info
        },
        overrideColorHex: alerts.overrideColorHex
    } satisfies WorkerMonitorAirportThresholdAlerts;
}

function toWorkerMonitorLevel(
    info: ThresholdInfo,
    effective: AirportThresholdEffective | AircraftThresholdEffective | null,
    level: ThresholdLevel
): WorkerThresholdLevel | null {
    const key = levelToKeyMap[level];
    const levelObject = effective?.[key].value;
    if (!levelObject) {
        return null;
    }

    let commonValue = levelObject.value;

    if (info.controlType === ThresholdControlType.input) {
        if (info.decimal) {
            commonValue /= DECIMAL_THRESHOLD_DIVISOR;
        }

        if (levelObject.unitIndex !== null) {
            if (levelObject.unitIndex in info.customUnits) {
                commonValue /= info.customUnits[levelObject.unitIndex].multiplier;
            }
        }
    }

    return {
        ...levelObject,
        level,
        commonUnitValue: commonValue,
        rawValue: levelObject.value
    };
}

function anyLevelDefined(effective: AircraftThresholdEffective) {
    return (
        effective.info.value !== null ||
        effective.watch.value !== null ||
        effective.warning.value !== null ||
        effective.critical.value !== null ||
        effective.emergency.value !== null
    );
}
